import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Link,
} from "@mui/material";
import { PrivacyTip as PrivacyIcon } from "@mui/icons-material";

interface PrivacyPolicyAcceptanceDialogProps {
  open: boolean;
  onAccept: () => Promise<void>;
  onDecline: () => void;
}

export default function PrivacyPolicyAcceptanceDialog({
  open,
  onAccept,
  onDecline,
}: PrivacyPolicyAcceptanceDialogProps) {
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleAccept = async () => {
    if (!accepted) {
      setError(
        "You must accept the privacy policy to continue using your account",
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      await onAccept();
    } catch (err: any) {
      setError(err.message || "Failed to save your acceptance");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onDecline();
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PrivacyIcon color="primary" fontSize="large" />
          <Typography variant="h5" component="span" fontWeight="bold">
            Privacy Policy Update Required
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          To comply with GDPR (DSGVO) regulations, we need your explicit consent
          to continue processing your personal data.
        </Alert>

        <Typography variant="body1" paragraph>
          We have updated our privacy policy to ensure full compliance with the
          General Data Protection Regulation (GDPR / DSGVO). To continue using
          your Stuttgart Padel League account, you must review and accept our
          privacy policy.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          What data do we collect?
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li>
            <Typography variant="body2">
              Your name, email, phone number (for league management)
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Match results and statistics (for rankings)
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Profile picture (optional, displayed publicly)
            </Typography>
          </li>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Your rights:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li>
            <Typography variant="body2">
              Right to access your personal data
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Right to request data deletion ("Right to be Forgotten")
            </Typography>
          </li>
          <li>
            <Typography variant="body2">Right to export your data</Typography>
          </li>
          <li>
            <Typography variant="body2">
              Right to choose a display name instead of your real name
            </Typography>
          </li>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            You can manage these privacy settings at any time by clicking the{" "}
            <strong>Gear icon (⚙️)</strong> in the top right corner of the page.
          </Typography>
        </Alert>

        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: "background.default",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={accepted}
                onChange={(e) => {
                  setAccepted(e.target.checked);
                  setError("");
                }}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I have read and accept the{" "}
                <Link
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontWeight: "bold" }}
                >
                  Privacy Policy
                </Link>{" "}
                and consent to the processing of my personal data as described.
                *
              </Typography>
            }
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body2" fontWeight="bold">
            Note: If you decline, you will be logged out and your account will
            remain inactive until you accept the privacy policy.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onDecline} disabled={loading} color="error">
          Decline & Logout
        </Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          disabled={!accepted || loading}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {loading ? "Accepting..." : "Accept & Continue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
